import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

/**
 * Per-user news source selection (NEWS_FEEDS_ADMIN_PRD.md §5.10).
 *
 * Shared by the Pro profile and the citizen profile — the data contract is
 * the same for both even though the two surfaces render nothing alike.
 *
 * The rule this hook exists to protect: an empty selection means "no
 * preference — show every enabled feed", NOT "show nothing". `usingDefault`
 * carries that distinction from the API, so a picker can render "all sources"
 * honestly instead of pre-ticking every box — which would silently convert a
 * default reader into a curated one the moment they saved, and freeze them
 * out of every feed added afterwards.
 */

export interface SelectableFeed {
  id: string;
  name: string;
  publisher: string | null;
  category: string;
  region: string;
  description: string | null;
}

export function useNewsFeedSelection() {
  const queryClient = useQueryClient();

  const available = useQuery({
    queryKey: ['news-feeds-available'],
    queryFn: async () => {
      const response = await api.get('/news-feeds/available');
      return (response.data.data ?? []) as SelectableFeed[];
    },
    staleTime: 10 * 60 * 1000,
  });

  const selection = useQuery({
    queryKey: ['news-feeds-selection'],
    queryFn: async () => {
      const response = await api.get('/news-feeds/selection');
      return (response.data.data ?? { selectedIds: [], usingDefault: true }) as {
        selectedIds: string[];
        usingDefault: boolean;
      };
    },
  });

  const save = useMutation({
    mutationFn: (selectedIds: string[]) => api.put('/news-feeds/selection', { selectedIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-feeds-selection'] });
      // The widget's contents change with the selection, so every surface
      // reading news has to re-fetch — Pro home, Pro dashboard card, and the
      // citizen news strip all key off these.
      queryClient.invalidateQueries({ queryKey: ['citizen-news'] });
      queryClient.invalidateQueries({ queryKey: ['proHome', 'news'] });
    },
  });

  const usingDefault = selection.data?.usingDefault ?? true;
  const selectedIds = selection.data?.selectedIds ?? [];

  /**
   * What the UI should show as ticked. On the default, every feed reads as
   * on — which is true, the reader is seeing all of them — while
   * `usingDefault` stays available so the UI can say *why*.
   */
  const effectiveIds = usingDefault ? (available.data ?? []).map((feed) => feed.id) : selectedIds;

  return {
    feeds: available.data ?? [],
    selectedIds,
    effectiveIds,
    usingDefault,
    loading: available.isLoading || selection.isLoading,
    error: available.error || selection.error,
    saving: save.isLoading,
    save: save.mutate,
    saveAsync: save.mutateAsync,
  };
}
